import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Link,
  makeStyles,
  MessageBar,
  MessageBarBody,
  Spinner,
  Text,
  tokens,
} from '@fluentui/react-components';
import { DocumentRegular, OpenRegular } from '@fluentui/react-icons';
import { insertDocxBlob } from '../../word/selection';
import { getAllTemplateUsage, incrementTemplateUsage } from '../../services/templateUsage';
import {
  fetchRootTemplates,
  fetchTemplateDocxContent,
  TemplateItem,
} from '../../services/templates';
import { AuthUser, generateMagicLink, refreshTokens } from '../../services/auth';
import { appUrl } from '../../services/navigation';
import TemplateInsertPrompt from './TemplateInsertPrompt';

/* global Office, window */

interface TemplatesTabProps {
  user: AuthUser | null;
  onAuthChange: (user: AuthUser | null) => void;
  onRequestLogin: () => void;
}

function openExternal(url: string): void {
  try {
    if (typeof Office !== 'undefined' && Office.context?.ui?.openBrowserWindow) {
      Office.context.ui.openBrowserWindow(url);
      return;
    }
  } catch {
    /* fall through */
  }
  window.open(url, '_blank', 'noopener,noreferrer');
}

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  intro: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
  },
  templateRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '6px 8px',
    borderRadius: tokens.borderRadiusSmall,
    background: 'none',
    border: 'none',
    width: '100%',
    boxSizing: 'border-box',
    color: tokens.colorNeutralForeground1,
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground2,
    },
  },
  templateIcon: {
    color: tokens.colorBrandForeground1,
    flexShrink: 0,
    marginTop: '1px',
  },
  templateBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    minWidth: 0,
    flex: 1,
    textAlign: 'left',
  },
  templateName: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
  },
  templateUsage: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
    fontStyle: 'italic',
    marginTop: '2px',
  },
  useBtn: {
    flexShrink: 0,
    minWidth: 'auto',
    paddingLeft: '10px',
    paddingRight: '10px',
    fontSize: tokens.fontSizeBase200,
    backgroundColor: '#1014f5',
    color: '#ffffff',
    border: '1px solid #1014f5',
    ':hover': {
      backgroundColor: '#2a2eff',
      border: '1px solid #2a2eff',
      color: '#ffffff',
    },
    ':hover:active': {
      backgroundColor: '#0a0dcc',
      border: '1px solid #0a0dcc',
      color: '#ffffff',
    },
  },
  manageRow: {
    marginTop: '12px',
    paddingTop: '10px',
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    fontSize: tokens.fontSizeBase200,
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  signedOutState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '16px',
    flex: 1,
    padding: '40px 16px',
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    textAlign: 'center',
  },
  signedOutText: {
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground1,
  },
  signedOutActions: {
    display: 'flex',
    gap: '8px',
  },
});

const TemplatesTab: React.FC<TemplatesTabProps> = ({ user, onAuthChange, onRequestLogin }) => {
  const styles = useStyles();
  const [templates, setTemplates] = useState<TemplateItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [insertingId, setInsertingId] = useState<string | null>(null);
  const [usage, setUsage] = useState<Record<string, number>>(() => getAllTemplateUsage());
  const [pendingTpl, setPendingTpl] = useState<TemplateItem | null>(null);

  useEffect(() => {
    if (!user) return;
    setLoading(true);
    setError(null);
    fetchRootTemplates(user.teamSlug, user.accessToken)
      .then(setTemplates)
      .catch(e => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
  }, [user]);

  const handleUseClick = useCallback(
    (tpl: TemplateItem) => {
      if (!user) return;
      setNotice(null);
      setPendingTpl(tpl);
    },
    [user],
  );

  const handlePromptResolve = useCallback(
    async (action: 'insert' | 'cancel') => {
      const tpl = pendingTpl;
      setPendingTpl(null);
      if (action === 'cancel' || !tpl || !user) return;
      setInsertingId(tpl.id);
      try {
        const blob = await fetchTemplateDocxContent(user.teamSlug, tpl.id, user.accessToken);
        await insertDocxBlob(blob);
        const newCount = incrementTemplateUsage(tpl.id);
        setUsage(prev => ({ ...prev, [tpl.id]: newCount }));
      } catch (e) {
        setNotice(`Couldn't insert template: ${e instanceof Error ? e.message : String(e)}`);
      } finally {
        setInsertingId(null);
      }
    },
    [pendingTpl, user],
  );

  const handleManageTemplates = async () => {
    if (!user) {
      onRequestLogin();
      return;
    }
    const freshUser = await refreshTokens(user, onAuthChange);
    if (!freshUser) return;
    const destination = appUrl(`${encodeURIComponent(freshUser.teamSlug)}/templates`);
    const magicLinkUrl = await generateMagicLink(freshUser.accessToken);
    openExternal(`${magicLinkUrl}?returnUrl=${encodeURIComponent(destination)}`);
  };

  return (
    <div className={styles.root}>
      <TemplateInsertPrompt
        open={pendingTpl !== null}
        templateName={pendingTpl?.name ?? null}
        onResolve={handlePromptResolve}
      />
      {user ? (
        <>
          <Text className={styles.intro}>
            Templates from your Signus library. Click "Use template" to insert one.
          </Text>

          {notice && (
            <MessageBar intent="warning">
              <MessageBarBody>{notice}</MessageBarBody>
            </MessageBar>
          )}

          {loading && <Spinner size="small" label="Loading templates…" />}

          {error && (
            <MessageBar intent="error">
              <MessageBarBody>{error}</MessageBarBody>
            </MessageBar>
          )}

          {!loading &&
            !error &&
            (templates.length === 0 ? (
              <Text className={styles.intro}>No templates found.</Text>
            ) : (
              templates.map(tpl => {
                const count = usage[tpl.id] ?? 0;
                return (
                  <div key={tpl.id} className={styles.templateRow}>
                    <DocumentRegular className={styles.templateIcon} />
                    <div className={styles.templateBody}>
                      <span className={styles.templateName}>{tpl.name}</span>
                      {count > 0 && (
                        <span className={styles.templateUsage}>
                          Used {count} time{count === 1 ? '' : 's'}
                        </span>
                      )}
                    </div>
                    <Button
                      className={styles.useBtn}
                      size="small"
                      onClick={() => handleUseClick(tpl)}
                      disabled={insertingId !== null}
                      icon={insertingId === tpl.id ? <Spinner size="tiny" /> : undefined}
                    >
                      {insertingId === tpl.id ? 'Inserting…' : 'Use template'}
                    </Button>
                  </div>
                );
              })
            ))}
        </>
      ) : (
        <div className={styles.signedOutState}>
          <Text className={styles.signedOutText}>Sign in to sync your templates.</Text>
          <div className={styles.signedOutActions}>
            <Button appearance="primary" onClick={onRequestLogin}>
              Log in
            </Button>
            <Button appearance="secondary" onClick={() => openExternal(appUrl('join'))}>
              Create account
            </Button>
          </div>
        </div>
      )}

      <div className={styles.manageRow}>
        <OpenRegular />
        <Link onClick={handleManageTemplates}>Manage templates</Link>
      </div>
    </div>
  );
};

export default TemplatesTab;
